import { ChangeDetectionStrategy, Component, Input } from '@angular/core'
import {
  ExtraOptions,
  ICoreConfig,
  IInnerDimensions,
  ISpecialConnection,
  ISvgScales,
  SVGComponents,
  SvgNode,
} from '../core/culture-index-chart.model'
import { easeLinearAnimation, getGroupClassNames, getOneOfDotsType, triangleSymbol } from '../utils/d3-utils'

type DrawCLineArgs = ISvgScales & Pick<IInnerDimensions, 'innerHeight'> & ExtraOptions & { line: ISpecialConnection }

@Component({
  selector: 'culture-index-chart-c-lines',
  standalone: true,
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CultureIndexChartCLinesComponent {
  @Input({ required: true }) coreConfig!: ICoreConfig

  /**
   * Draws special lines used for culture index target values
   */
  drawCLine(svg: SvgNode, args: DrawCLineArgs, loading?: boolean) {
    // Add special vertical arrow - fixed position of arrow to top of line
    const { xScale, yScale, innerHeight, line, compare } = args
    const specialCon = svg.append('g').attr('class', `${getOneOfDotsType(compare)}${SVGComponents.CLINE}`)

    specialCon
      .selectAll('line')
      .data([line])
      .enter()
      .append('line')
      .attr('x1', connection => xScale(connection.position + 2) + this.coreConfig.margin.left)
      .attr('y1', yScale(0.85))
      .attr('x2', connection => xScale(connection.position + 2) + this.coreConfig.margin.left)
      .attr('y2', yScale(4.8))
      .attr('stroke', connection => (loading ? '#e0e0e0' : connection.color || 'red'))
      .attr('stroke-width', connection => connection.size || 2)
      .attr('stroke-dasharray', connection => (connection.dashed ? '6, 3' : '0, 0'))
      .style('opacity', 0)
      .transition()
      .duration(300)
      .delay(1600)
      .transition()
      .duration(500)
      .delay(200)
      .ease(easeLinearAnimation)
      .style('opacity', 1)

    const arrowScale = innerHeight * 0.2

    specialCon
      // Arrow at the top of the line (fixed to point downward)
      .append('path')
      .attr('d', triangleSymbol(line.size, arrowScale))
      .attr('transform', () => `translate(${xScale(line.position + 2) + this.coreConfig.margin.left}, ${yScale(0.85)})`)
      .attr('fill', () => (loading ? '#e0e0e0' : line.color || 'red'))
      .style('opacity', 0)
      .transition()
      .duration(300)
      .delay(1600)
      .transition()
      .duration(500)
      .delay(200)
      .ease(easeLinearAnimation)
      .style('opacity', 1)
  }

  /**
   * Updates special lines used for culture index target values
   */
  updateCLine(svg: SvgNode, args: DrawCLineArgs, loading?: boolean) {
    const { xScale, yScale, innerHeight, line, compare } = args
    const arrowScale = innerHeight * 0.2
    const marginLeft = this.coreConfig.margin.left

    const group = getGroupClassNames([SVGComponents.CLINE], [getOneOfDotsType(compare)])[0]
    const lines = svg.selectAll(`${group} > line`).data([line])

    lines
      .transition()
      .duration(500)
      .attr('x1', d => xScale(d.position + 2) + marginLeft)
      .attr('y1', yScale(0.85))
      .attr('x2', d => xScale(d.position + 2) + marginLeft)
      .attr('y2', yScale(4.8))
      .attr('stroke', d => (loading ? '#e0e0e0' : d.color || 'red'))
      .attr('stroke-width', d => d.size || 2)
      .attr('stroke-dasharray', d => (d.dashed ? '6, 3' : '0, 0'))
      .style('opacity', 1)

    const paths = svg.selectAll(`${group} > path`).data([line])

    paths
      .transition()
      .duration(500)
      .attr('d', d => triangleSymbol(d.size, arrowScale)())
      .attr('transform', d => `translate(${xScale(d.position + 2) + marginLeft}, ${yScale(0.85)})`)
      .attr('fill', d => (loading ? '#e0e0e0' : d.color || 'red'))
      .style('opacity', 1)
  }
}
